import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SEATS_3P,
  SEATS_4P,
  calculateMatch,
  isBankrupt,
  membersRequiringBuster,
  membersRequiringSeat,
  seatLabel,
  toRuleSnapshot,
  validateMatch,
} from '@mahjong/core';
import type { ResultInput, Seat } from '@mahjong/core';
import {
  AppBar,
  Body,
  Button,
  Chip,
  ErrorBox,
  InfoBox,
  Label,
  ListCard,
  RankBadge,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { useGroup, useGroupToken } from '../hooks/useGroup';
import { api, ApiError } from '../lib/api';
import { formatPoint, formatScore } from '../lib/format';

/** 素点のステッパー。4列に並ぶので文字は詰めて置く */
function StepButton({
  onClick,
  children,
  tone = 'default',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'default' | 'clear' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      className={cx(
        'rounded-md border py-2 text-[11px] tabular-nums transition active:bg-bone',
        tone === 'clear'
          ? 'border-line bg-white text-ink-faint'
          : 'border-line bg-white text-ink-soft',
      )}
    >
      {children}
    </button>
  );
}

/**
 * 対局を記録（§4.4 / 画面案 3・4・5）。
 *
 * 素点の入力が主作業なので、名前は左に小さく数字を右に大きく置く。席と
 * 「飛ばした人」は普段は出さず、同点・飛びを検出したときだけ該当行に開く。
 * 判定は @mahjong/core と共有しているため、ここでのプレビューと保存時の
 * サーバー計算は必ず一致する。
 */
export function MatchInputPage() {
  const { groupId, matchId } = useParams<{ groupId: string; matchId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useGroupToken(groupId);
  const group = useGroup(groupId, token);

  // matchId があれば編集（R-04）。ルールは対局のスナップショットを使うため、
  // グループの既定ルールを後から変えていても計算は当時のまま。
  const isEdit = Boolean(matchId);
  const existing = useQuery({
    queryKey: ['match', groupId, matchId],
    queryFn: ({ signal }) => api.getMatch(groupId!, token!, matchId!, signal),
    enabled: Boolean(isEdit && groupId && matchId && token),
  });

  const bundle = group.data;

  // 複数ルールがある場合は選べる（§4.3）。編集時は対局のスナップショットを使うため
  // 選択させない
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const ruleSet =
    bundle?.rules.find((r) => r.ruleId === (selectedRuleId ?? bundle.group.defaultRuleId)) ??
    bundle?.rules[0];
  const rule = useMemo(() => (ruleSet ? toRuleSnapshot(ruleSet) : null), [ruleSet]);

  const activeMembers = useMemo(
    () => (bundle?.members ?? []).filter((m) => m.active),
    [bundle],
  );

  // 参加者。既定は並び順の先頭から人数分
  const [participants, setParticipants] = useState<string[] | null>(null);
  const resolvedParticipants = useMemo(() => {
    if (participants) return participants;
    if (!rule) return [];
    return activeMembers.slice(0, rule.playerCount).map((m) => m.memberId);
  }, [participants, activeMembers, rule]);

  const [scores, setScores] = useState<Record<string, string>>({});
  const [seats, setSeats] = useState<Record<string, Seat | null>>({});
  const [busters, setBusters] = useState<Record<string, string | null>>({});
  const [memo, setMemo] = useState('');
  const [activeRow, setActiveRow] = useState<string | null>(null);
  const [showSeats, setShowSeats] = useState(false);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // 編集時は既存の値を1度だけ流し込む
  const loadedMatch = existing.data;
  useEffect(() => {
    if (!loadedMatch || hydrated) return;
    setParticipants(loadedMatch.results.map((r) => r.memberId));
    setScores(Object.fromEntries(loadedMatch.results.map((r) => [r.memberId, String(r.rawScore)])));
    setSeats(Object.fromEntries(loadedMatch.results.map((r) => [r.memberId, r.seat ?? null])));
    // bustedBy は飛んだ人にだけ意味がある。飛んでいない人の null を持ち回っても
    // 表示条件に掛からないので、そのまま入れておく
    setBusters(
      Object.fromEntries(loadedMatch.results.map((r) => [r.memberId, r.bustedBy ?? null])),
    );
    setMemo(loadedMatch.memo ?? '');
    setHydrated(true);
  }, [loadedMatch, hydrated]);

  /** 参加メンバーの入れ替え。人数分そろうまで保存はできない */
  function toggleParticipant(memberId: string) {
    const current = participants ?? resolvedParticipants;
    if (current.includes(memberId)) {
      setParticipants(current.filter((id) => id !== memberId));
    } else if (rule && current.length < rule.playerCount) {
      setParticipants([...current, memberId]);
    }
  }

  const nameOf = (id: string) => activeMembers.find((m) => m.memberId === id)?.name ?? '?';

  // --- 入力を core の形に変換 ----------------------------------------------
  const inputs: ResultInput[] = useMemo(
    () =>
      resolvedParticipants.map((memberId) => {
        const raw = scores[memberId];
        const parsed = raw === undefined || raw.trim() === '' ? Number.NaN : Number(raw);
        return {
          memberId,
          nameSnapshot: nameOf(memberId),
          seat: seats[memberId] ?? null,
          rawScore: Number.isFinite(parsed) ? parsed : Number.NaN,
          // キーの有無で「未選択」と「該当なし」を区別する（§4.4.1）
          ...(memberId in busters ? { bustedBy: busters[memberId] ?? null } : {}),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedParticipants, scores, seats, busters, activeMembers],
  );

  const allFilled = inputs.every((i) => Number.isFinite(i.rawScore));
  const validation = rule && allFilled ? validateMatch(inputs, rule) : null;
  const preview = rule && validation?.ok ? calculateMatch(inputs, rule) : null;

  const seatRequired = allFilled ? new Set(membersRequiringSeat(inputs)) : new Set<string>();
  const busterRequired = rule && allFilled ? new Set(membersRequiringBuster(inputs, rule)) : new Set<string>();
  const bankrupts = rule && allFilled ? inputs.filter((i) => isBankrupt(i.rawScore, rule)) : [];

  const expectedSum = rule ? rule.startPoints * rule.playerCount : 0;
  const currentSum = inputs.reduce((s, i) => s + (Number.isFinite(i.rawScore) ? i.rawScore : 0), 0);
  const sumDiff = expectedSum - currentSum;

  const validSeats: readonly Seat[] = rule?.playerCount === 3 ? SEATS_3P : SEATS_4P;
  const seatTaken = (seat: Seat, exceptId: string) =>
    resolvedParticipants.some((id) => id !== exceptId && seats[id] === seat);

  // --- 保存 -----------------------------------------------------------------
  const save = useMutation({
    mutationFn: () => {
      const payload = {
        memo: memo.trim() || undefined,
        // 編集時はサーバーが対局のスナップショットを使うため送らない
        ...(isEdit ? {} : { ruleId: ruleSet?.ruleId }),
        results: inputs.map(({ nameSnapshot: _n, ...r }) => r),
      };
      if (isEdit) {
        return api.updateMatch(groupId!, token!, matchId!, {
          ...payload,
          version: loadedMatch!.version,
        });
      }
      return api.createMatch(groupId!, token!, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['matches', groupId] });
      void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      void queryClient.invalidateQueries({ queryKey: ['match', groupId, matchId] });
      navigate(isEdit ? `/g/${groupId}/matches/${matchId}` : `/g/${groupId}`);
    },
  });

  /** 連続入力（R-08）。メンバーと席は引き継ぎ、素点と飛びだけ消す */
  function saveAndContinue() {
    save.mutate(undefined, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['matches', groupId] });
        void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
        setScores({});
        setBusters({});
        setMemo('');
        setActiveRow(null);
      },
    });
  }

  function step(memberId: string, delta: number) {
    setScores((prev) => {
      const current = Number(prev[memberId] ?? '0');
      const base = Number.isFinite(current) ? current : 0;
      return { ...prev, [memberId]: String(base + delta) };
    });
  }

  /**
   * 符号を反転する。
   *
   * 素点入力は数値キーパッドを出す指定（§9.3）だが、そのキーパッドには
   * マイナスキーが無い端末が多く、飛んだ人の素点を手で打てなくなる。
   * 空欄のときは "-" を置いて、これから負の数を入力できるようにする。
   */
  function negate(memberId: string) {
    setScores((prev) => {
      const raw = (prev[memberId] ?? '').trim();
      if (raw === '' || raw === '-') {
        return { ...prev, [memberId]: raw === '-' ? '' : '-' };
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) return prev;
      return { ...prev, [memberId]: String(-n) };
    });
  }

  function clearScore(memberId: string) {
    setScores((prev) => ({ ...prev, [memberId]: '' }));
  }

  // --- 保存ボタンの文言。残タスクをそのまま出す（画面案4） -------------------
  const blocker = (() => {
    if (resolvedParticipants.length !== rule?.playerCount) {
      return `参加メンバーを${rule?.playerCount ?? 4}人選んでください`;
    }
    if (!allFilled) return '素点を入力してください';
    if (sumDiff !== 0) {
      return sumDiff > 0
        ? `合計が${formatScore(sumDiff)}点足りません`
        : `合計が${formatScore(-sumDiff)}点多すぎます`;
    }
    const missingSeat = [...seatRequired].filter((id) => !seats[id]);
    if (missingSeat.length > 0) return `${nameOf(missingSeat[0]!)}の席を選んでください`;
    const missingBuster = [...busterRequired];
    if (missingBuster.length > 0) return `${nameOf(missingBuster[0]!)}を飛ばした人を選んでください`;
    if (validation && !validation.ok) return validation.errors[0]!.message;
    return null;
  })();

  if (!token) {
    return (
      <Screen>
        <AppBar title="対局を記録" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (group.isLoading || !rule || !bundle || (isEdit && !loadedMatch)) {
    return (
      <Screen>
        <AppBar title={isEdit ? '対局を編集' : '対局を記録'} backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  if (activeMembers.length < rule.playerCount) {
    return (
      <Screen>
        <AppBar title="対局を記録" backTo={`/g/${groupId}`} />
        <Body>
          <ErrorBox
            title="メンバーが足りません"
            message={`${rule.playerCount}人打ちの記録には${rule.playerCount}人以上のメンバーが必要です（現在 ${activeMembers.length}人）。`}
          />
        </Body>
      </Screen>
    );
  }

  return (
    <Screen>
      <AppBar
        title={isEdit ? '対局を編集' : '対局を記録'}
        backTo={isEdit ? `/g/${groupId}/matches/${matchId}` : `/g/${groupId}`}
      />

      <Body className="gap-2.5 pb-6">
        {/* --- 適用ルール --- */}
        {!isEdit && bundle.rules.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {bundle.rules.map((r) => (
              <Chip
                key={r.ruleId}
                selected={r.ruleId === ruleSet?.ruleId}
                onClick={() => setSelectedRuleId(r.ruleId)}
              >
                {r.name}
              </Chip>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-bone px-3 py-2 text-xs text-ink-soft">
          <span>{ruleSet?.name}</span>
          <span className="text-line">|</span>
          <span>
            {rule.startPoints.toLocaleString('ja-JP')} / {rule.returnPoints.toLocaleString('ja-JP')}
          </span>
          <span className="text-line">|</span>
          <span>ウマ {rule.uma.map((u) => formatPoint(u)).join(' ')}</span>
        </div>

        {/* --- 参加メンバー（§9.2） --- */}
        <div className="flex items-baseline justify-between">
          <Label>参加メンバー</Label>
          <button
            type="button"
            onClick={() => setShowMemberPicker((v) => !v)}
            className="text-[12.5px] text-felt underline underline-offset-2"
          >
            {showMemberPicker ? '閉じる' : '入れ替える'}
          </button>
        </div>
        {showMemberPicker && (
          <div className="flex flex-wrap gap-1.5">
            {activeMembers.map((m) => {
              const selected = resolvedParticipants.includes(m.memberId);
              const full = resolvedParticipants.length >= rule.playerCount;
              return (
                <Chip
                  key={m.memberId}
                  selected={selected}
                  disabled={!selected && full}
                  onClick={() => toggleParticipant(m.memberId)}
                  className={!selected && full ? 'opacity-40' : undefined}
                >
                  {m.name}
                </Chip>
              );
            })}
          </div>
        )}

        {/* --- 同点の案内（画面案4） --- */}
        {seatRequired.size > 0 && (
          <ErrorBox
            message={`${[...seatRequired].map(nameOf).join('と')}が同点です。順位を決めるため、${seatRequired.size}人の席を選んでください。`}
          />
        )}

        {/* --- 飛びの案内（画面案5） --- */}
        {bankrupts.length > 0 && rule.bankruptcy.enabled && (
          <InfoBox>
            {bankrupts.map((b) => nameOf(b.memberId)).join('と')}が飛びました。
            {rule.bankruptcy.payTo === 'buster'
              ? `飛ばした人が${rule.bankruptcy.point}ptを受け取ります。`
              : `トップが${rule.bankruptcy.point}ptを受け取ります。`}
          </InfoBox>
        )}

        {/* --- 入力行 --- */}
        <ListCard className="py-1">
          {resolvedParticipants.map((memberId, i) => {
            const isActive = activeRow === memberId;
            const needsSeat = seatRequired.has(memberId);
            const isBust =
              allFilled && rule.bankruptcy.enabled && isBankrupt(inputs[i]!.rawScore, rule);
            const needsBuster = rule.bankruptcy.payTo === 'buster' && isBust;

            return (
              <div key={memberId} className={cx(i > 0 && 'border-t border-line')}>
                {/*
                  余白は選択状態に関わらず常に確保する。選択時だけ -mx/px を足すと
                  行の内側の幅が変わり、右寄せしている数字が横に跳ねてしまう。
                */}
                <button
                  type="button"
                  onClick={() => setActiveRow(isActive ? null : memberId)}
                  className={cx(
                    '-mx-2 flex w-full items-center gap-2.5 rounded-lg px-2 py-2.5 text-left transition-colors',
                    isActive && 'bg-white shadow-[inset_0_0_0_1.5px_var(--color-felt)]',
                  )}
                >
                  <span className="text-[15px] font-bold">{nameOf(memberId)}</span>
                  <input
                    value={scores[memberId] ?? ''}
                    onChange={(e) =>
                      setScores((prev) => ({
                        ...prev,
                        [memberId]: e.target.value.replace(/[^0-9-]/g, ''),
                      }))
                    }
                    onFocus={() => setActiveRow(memberId)}
                    inputMode="numeric"
                    placeholder="0"
                    aria-label={`${nameOf(memberId)}の素点`}
                    className={cx(
                      'ml-auto w-32 bg-transparent text-right text-[21px] font-black tabular-nums outline-none',
                      (scores[memberId] ?? '') === '' && 'placeholder:text-[#C3BCA9]',
                      Number(scores[memberId]) < 0 && 'pt-minus',
                    )}
                  />
                </button>

                {isActive && (
                  <div className="grid grid-cols-4 gap-1.5 pb-2">
                    {[-10000, -1000, -100].map((d) => (
                      <StepButton key={d} onClick={() => step(memberId, d)}>
                        −{Math.abs(d)}
                      </StepButton>
                    ))}
                    <StepButton onClick={() => negate(memberId)} aria-label="符号を反転">
                      ±
                    </StepButton>
                    {[100, 1000, 10000].map((d) => (
                      <StepButton key={d} onClick={() => step(memberId, d)}>
                        +{d}
                      </StepButton>
                    ))}
                    <StepButton onClick={() => clearScore(memberId)} tone="clear">
                      クリア
                    </StepButton>
                  </div>
                )}

                {/* 席セレクタ。同点時のみ自動で開く（§4.4.3） */}
                {(needsSeat || showSeats) && (
                  <div className="pb-2.5">
                    <div className="mb-1.5 text-[11.5px] text-ink-soft">
                      席{needsSeat && <span className="ml-1 text-red">必須</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {validSeats.map((s) => (
                        <Chip
                          key={s}
                          selected={seats[memberId] === s}
                          tone={needsSeat && !seats[memberId] ? 'warn' : 'default'}
                          disabled={seatTaken(s, memberId)}
                          onClick={() =>
                            setSeats((prev) => ({
                              ...prev,
                              [memberId]: prev[memberId] === s ? null : s,
                            }))
                          }
                          className={seatTaken(s, memberId) ? 'opacity-30' : undefined}
                        >
                          {seatLabel(s)}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {/* 飛ばした人。飛び検出時のみ出る（§4.4.1） */}
                {needsBuster && (
                  <div className="pb-2.5">
                    <div className="mb-1.5 text-[11.5px] text-ink-soft">
                      飛ばした人
                      {busterRequired.has(memberId) && <span className="ml-1 text-red">必須</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {resolvedParticipants
                        .filter((id) => id !== memberId)
                        .map((id) => (
                          <Chip
                            key={id}
                            selected={busters[memberId] === id}
                            tone={busterRequired.has(memberId) ? 'warn' : 'default'}
                            onClick={() => setBusters((prev) => ({ ...prev, [memberId]: id }))}
                          >
                            {nameOf(id)}
                          </Chip>
                        ))}
                      <Chip
                        selected={memberId in busters && busters[memberId] === null}
                        tone={busterRequired.has(memberId) ? 'warn' : 'default'}
                        onClick={() => setBusters((prev) => ({ ...prev, [memberId]: null }))}
                      >
                        該当なし
                      </Chip>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </ListCard>

        {/* --- 席を任意入力するトグル（§9.3） --- */}
        {seatRequired.size === 0 && (
          <button
            type="button"
            onClick={() => setShowSeats((v) => !v)}
            className="self-start text-[12.5px] text-ink-faint underline underline-offset-2"
          >
            {showSeats ? '席の入力を隠す' : '席を入力する'}
          </button>
        )}

        {/* --- 合計の過不足（常時表示、§9.3） --- */}
        <div
          className={cx(
            'flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium',
            sumDiff === 0 ? 'bg-[#E8F0E6] text-ok' : 'bg-[#F8E7E4] text-red',
          )}
        >
          {sumDiff === 0 ? (
            <>
              <span>✓</span>
              <span>合計 {formatScore(expectedSum)} 点</span>
            </>
          ) : (
            <>
              <span>!</span>
              <span>
                {sumDiff > 0
                  ? `${formatScore(sumDiff)}点 足りません`
                  : `${formatScore(-sumDiff)}点 多すぎます`}
                （現在 {formatScore(currentSum)} 点）
              </span>
            </>
          )}
        </div>

        {/* --- 計算結果プレビュー（§4.4 R-02） --- */}
        {preview && (
          <div className="rounded-xl bg-felt px-3.5 py-3 text-bone-hi">
            <div className="mb-1.5 text-[11.5px] text-felt-sub">計算結果</div>
            {preview.results.map((r) => (
              <div key={r.memberId} className="flex items-center gap-2.5 py-1 text-[13.5px]">
                <RankBadge rank={r.rank} className="size-[19px] text-[11px]" />
                <span>{r.nameSnapshot}</span>
                {r.tiedButLower && (
                  <span className="rounded border border-[#E4CE9A] px-1 text-[10px] text-gold">
                    同点（席順で下位）
                  </span>
                )}
                <span
                  className={cx(
                    'ml-auto text-base font-black tabular-nums',
                    r.totalPoint >= 0 ? 'text-[#7FD9A8]' : 'text-[#F09A92]',
                  )}
                >
                  {formatPoint(r.totalPoint)}
                </span>
              </div>
            ))}
            {preview.zeroSumAdjustment !== 0 && (
              <div className="mt-1.5 text-[11px] text-felt-sub">
                端数の差 {formatPoint(preview.zeroSumAdjustment)} を1位に加算しています
              </div>
            )}
          </div>
        )}

        {/* --- メモ（R-06） --- */}
        <div>
          <Label>メモ（任意）</Label>
          <input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            maxLength={100}
            placeholder="近所の雀荘"
            className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[14px] outline-none focus:border-felt"
          />
        </div>

        {save.isError && (
          <ErrorBox
            title={
              save.error instanceof ApiError && save.error.isConflict
                ? '他の端末で先に更新されました'
                : '保存できませんでした'
            }
            message={save.error instanceof ApiError ? save.error.message : '通信に失敗しました'}
            action={
              save.error instanceof ApiError && save.error.isConflict ? (
                <button
                  type="button"
                  onClick={() => {
                    // 最新を取り直してから編集し直してもらう（§10 同時編集）
                    setHydrated(false);
                    void existing.refetch();
                    save.reset();
                  }}
                  className="font-bold underline underline-offset-2"
                >
                  最新の内容を読み込む
                </button>
              ) : undefined
            }
          />
        )}

        {isEdit && (
          <Label className="leading-relaxed">
            対局日時は変更できません。日時を直す場合は削除して記録し直してください。
          </Label>
        )}

        <div className="mt-auto flex flex-col gap-2 pt-2">
          <Button onClick={() => save.mutate()} disabled={blocker !== null || save.isPending}>
            {save.isPending
              ? '保存しています…'
              : (blocker ?? (isEdit ? '更新する' : '記録する'))}
          </Button>
          {!isEdit && blocker === null && (
            <Button variant="ghost" onClick={saveAndContinue} disabled={save.isPending}>
              記録して続けて入力
            </Button>
          )}
        </div>
      </Body>
    </Screen>
  );
}
